// /src/hooks/useAuctionData.js
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { trackPromise } from "react-promise-tracker";
import { useDialogBox } from "./useDialogBox";
import AuctionDataService from "../services/AuctionService";

import {
	retrievePackages,
	retrieveOnePackage,
	retrieveOnePackageByUuid,
	retrieveOnePackageItem,
	getTopAutoBidsPerUser,
	setSelectedPkg,
	clearSelectedPkg,
	setSelectedPkgItem,
	clearSelectedPkgItem,
} from "../slices/auctionSlice";

import { prepareAuctionPackageObject } from "../utils/auctionUtils";
import { debugLog, errorLog } from "../utils/debugUtils";

// Custom hook centralizes Auction API logic
export const useAuctionsData = () => {
	const dispatch = useDispatch();
	const { withError } = useDialogBox();
	
	// Fetch all auction packages (for AuctionPackageList)
	const fetchAuctionPackages = useCallback(() => {
		debugLog(3, "[useAuctionsData][ACTION] fetchAuctionPackages", {});
		debugLog(2, "[useAuctionsData][FLOW] retrievePackages started", {});

		trackPromise(
			dispatch(retrievePackages())
				.unwrap()
				.then((response) => {
					// Auction list now in Redux: state.auctions.list
					debugLog(3, "[useAuctionsData][STATE] auction packages retrieved", {
						auctionPackages: response,
						count: Array.isArray(response) ? response.length : undefined,
					});
				})
				.catch((err) => {
					errorLog(2, "[useAuctionsData][FLOW] retrievePackages failed", { error: err });
					console.error("Failed to fetch auction packages", err);
				})
		);
	}, [dispatch]);

	// Fetch single auction package by id (for details/edit/deep linking)
	const fetchAuctionPackageByIdOrUuid = useCallback(
		(idOrUuid) => {
			debugLog(3, "[useAuctionsData][ACTION] fetchAuctionPackageByIdOrUuid", {
				idOrUuid: idOrUuid,
			});
			debugLog(2, "[useAuctionsData][FLOW] retrieveOnePackage started", { idOrUuid: idOrUuid });

			const action = Number.isNaN(Number(idOrUuid))
				? retrieveOnePackageByUuid(idOrUuid)
				: retrieveOnePackage(idOrUuid);

			trackPromise(
				dispatch(action)
					.unwrap()
					.then((response) => {
						debugLog(3, "[useAuctionsData][STATE] auction package retrieved", {
							preparedPackage: response,
						});
					})
					.catch((err) => {
						errorLog(2, "[useAuctionsData][FLOW] retrieveOnePackage failed", {
							idOrUuid: idOrUuid,
							error: err,
						});
						console.error("Failed to fetch auction package", err);
					})
			);
		},
		[dispatch]
	);

	// UI handler for list selection; triggers fetch and updates Redux
	const handleSelectAuctionPackage = useCallback(
		(auctionPackage) => {
			debugLog(3, "[useAuctionsData][ACTION] handleSelectAuctionPackage", {
				auctionPackage: auctionPackage,
			});
			fetchAuctionPackageByIdOrUuid(auctionPackage.id);
		},
		[fetchAuctionPackageByIdOrUuid]
	);

	// Fetch a single auction package item (optional, for item detail views)
	const fetchAuctionPackageItemById = useCallback(
		async (itemId) => {
			debugLog(3, "[useAuctionsData][ACTION] fetchAuctionPackageItemById", { itemId: itemId });
			debugLog(2, "[useAuctionsData][FLOW] retrieveOnePackageItem started", { itemId: itemId });

			// Return the tracked promise so callers can await completion of the item load.
			return trackPromise(
				dispatch(retrieveOnePackageItem(itemId))
					.unwrap()
					.then((response) => {
						debugLog(3, "[useAuctionsData][STATE] auction package item retrieved", {
							auctionPackageItem: response,
						});
						return response;
					})
					.catch((err) => {
						errorLog(2, "[useAuctionsData][FLOW] retrieveOnePackageItem failed", {
							itemId: itemId,
							error: err,
						});
						console.error("Failed to fetch auction package item", err);
						// Item retrieval is read-only, so retrying the same request is safe.
						withError(err, () => fetchAuctionPackageItemById(itemId), {
							fallbackMessage: "Package item retrieve failed.",
						});
						return null;
					})
			);
		},
		[dispatch, withError]
	);

	// Reconcile the package-level balances/bid history and the selected item's
	// auto-bid state after a live socket reconnect. Let the caller handle failures
	// so live actions can remain disabled until the user retries successfully.
	const refreshAuctionLiveState = useCallback(
		async (packageId, itemId) => {
			const [packageResponse, packageItemResponse] = await trackPromise(
				Promise.all([
					AuctionDataService.getOnePackage(packageId),
					AuctionDataService.getOnePackageItem(itemId),
				])
			);
			const preparedPackage = prepareAuctionPackageObject(packageResponse.data);

			// These reads intentionally avoid selection-updating Redux thunks. The
			// component applies this snapshot only after confirming it is still current.
			return { package: preparedPackage, packageItem: packageItemResponse.data };
		},
		[]
	);

	// Fetch top auto-bids for a given package
	const fetchTopAutoBids = useCallback(
		(packageId) => {
			debugLog(3, "[useAuctionsData][ACTION] fetchTopAutoBids", { packageId: packageId });
			debugLog(2, "[useAuctionsData][FLOW] getTopAutoBidsPerUser started", { packageId: packageId });

			return trackPromise(
				dispatch(getTopAutoBidsPerUser(packageId))
					.unwrap()
					.then((response) => {
						debugLog(3, "[useAuctionsData][STATE] top auto-bids retrieved", {
							topAutoBids: response,
							packageId,
						});

						return response;
					})
					.catch((err) => {
						errorLog(2, "[useAuctionsData][FLOW] getTopAutoBidsPerUser failed", {
							packageId: packageId,
							error: err,
						});
						console.error("Failed to fetch top auto-bids", err);
						// Top auto-bids retrieval is read-only, so retrying the same request is safe.
						withError(err, () => fetchTopAutoBids(packageId), {
							fallbackMessage: "Top auto-bids retrieve failed.",
						});
						return null;
					})
			);
		},
		[dispatch, withError]
	);

	const setSelectedPackage = useCallback(
		(pkg) => {
			debugLog(3, "[useAuctionsData][ACTION] setSelectedPackage", { auctionPackage: pkg });
			dispatch(setSelectedPkg(pkg));
		},
		[dispatch]
	);

	const clearSelectedPackage = useCallback(() => {
		debugLog(3, "[useAuctionsData][ACTION] clearSelectedPackage", {});
		dispatch(clearSelectedPkg());
	}, [dispatch]);

	const setSelectedPackageItem = useCallback(
		(item) => {
			debugLog(3, "[useAuctionsData][ACTION] setSelectedPackageItem", { packageItem: item });
			dispatch(setSelectedPkgItem(item));
		},
		[dispatch]
	);

	const clearSelectedPackageItem = useCallback(() => {
		debugLog(3, "[useAuctionsData][ACTION] clearSelectedPackageItem", {});
		dispatch(clearSelectedPkgItem());
	}, [dispatch]);

	return {
		fetchAuctionPackages,
		fetchAuctionPackageByIdOrUuid,
		handleSelectAuctionPackage,
		fetchAuctionPackageItemById,
		refreshAuctionLiveState,
		fetchTopAutoBids,
		setSelectedPackage,
		clearSelectedPackage,
		setSelectedPackageItem,
		clearSelectedPackageItem,
	};
};
